import { Component, forwardRef, Input, OnInit, OnDestroy } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormControl } from '@angular/forms';
import { debounceTime, distinctUntilChanged, Subject } from 'rxjs';
import { ThemeService } from 'src/app/services/theme.service';
import { takeUntil } from 'rxjs/operators';
import { TagService } from '@shared/services/api/tag.service';

@Component({
  selector: 'app-learning-tag-input',
  templateUrl: './learning-tag-input.component.html',
  styleUrls: ['./learning-tag-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => LearningTagInputComponent),
      multi: true
    }
  ]
})
export class LearningTagInputComponent implements ControlValueAccessor, OnInit, OnDestroy {
  @Input() placeholder = 'LEARNING.TAG_PLACEHOLDER';

  allTags: string[] = [];
  filteredOptions: string[] = [];
  selectedItems: string[] = [];
  searchControl = new FormControl('');
  isDropdownOpen = false;
  isLoading = false;
  errorMessage = '';
  isLight = true;
  currentSearchTerm = '';

  private searchTerms = new Subject<string>();
  private destroy$ = new Subject<void>();
  private onChange: (value: any) => void = () => {
    /* noop */
  };
  private _onTouched: () => void = () => {
    /* noop */
  };

  constructor(
    private _themeService: ThemeService,
    private tagService: TagService
  ) {}

  ngOnInit() {
    this._themeService.isLight$.pipe(takeUntil(this.destroy$)).subscribe((isLight) => (this.isLight = isLight));

    this.loadAllTags();

    this.searchTerms.pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$)).subscribe((term) => {
      this.currentSearchTerm = term;
      this.filterOptions(term);
      this.updateDropdownOptions();
    });

    this.searchControl.valueChanges.subscribe((value) => {
      if (typeof value === 'string') {
        this.searchTerms.next(value.trim());
      }
    });
  }

  private loadAllTags() {
    this.isLoading = true;
    this.tagService
      .getAll()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (tags) => {
          this.allTags = tags;
          this.isLoading = false;
        },
        error: (error) => {
          console.error('Failed to load tags:', error);
          this.errorMessage = 'Failed to load tags';
          this.isLoading = false;
        }
      });
  }

  private filterOptions(searchTerm: string) {
    if (!searchTerm) {
      this.filteredOptions = this.allTags.filter((tag) => !this.selectedItems.includes(tag));
    } else {
      const lowerSearchTerm = searchTerm.toLowerCase();
      this.filteredOptions = this.allTags.filter(
        (tag) => tag.toLowerCase().includes(lowerSearchTerm) && !this.selectedItems.includes(tag)
      );
    }
  }

  private updateDropdownOptions() {
    const hasExactMatch = this.filteredOptions.some(
      (option) => option.toLowerCase() === this.currentSearchTerm.toLowerCase()
    );

    this.isDropdownOpen = this.filteredOptions.length > 0 || (this.currentSearchTerm.length > 0 && !hasExactMatch);
  }

  get canAddNewTag(): boolean {
    if (!this.currentSearchTerm || this.currentSearchTerm.trim().length === 0) {
      return false;
    }

    const hasExactMatch = this.filteredOptions.some(
      (option) => option.toLowerCase() === this.currentSearchTerm.toLowerCase()
    );

    const alreadySelected = this.selectedItems.some(
      (item) => item.toLowerCase() === this.currentSearchTerm.toLowerCase()
    );

    return !hasExactMatch && !alreadySelected;
  }

  writeValue(value: string[]): void {
    this.selectedItems = value || [];
    if (this.allTags.length > 0) {
      this.filterOptions(this.currentSearchTerm);
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this._onTouched = fn;
  }

  selectItem(item: string) {
    if (!this.selectedItems.includes(item)) {
      this.selectedItems.push(item);
      this.onChange(this.selectedItems);
      this.filterOptions(this.currentSearchTerm);
    }
    this.searchControl.setValue('');
    this.isDropdownOpen = false;
  }

  addNewTag() {
    const tagName = this.currentSearchTerm.trim();
    if (tagName && this.canAddNewTag) {
      this.selectedItems.push(tagName);
      this.onChange(this.selectedItems);
      this.filterOptions('');
      this.searchControl.setValue('');
      this.isDropdownOpen = false;
    }
  }

  onSearchKeydown(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      event.preventDefault();
      if (this.canAddNewTag) {
        this.addNewTag();
      } else if (this.filteredOptions.length > 0) {
        this.selectItem(this.filteredOptions[0]);
      }
    }
  }

  removeItem(item: string) {
    this.selectedItems = this.selectedItems.filter((selected) => selected !== item);
    this.onChange(this.selectedItems);
    this.filterOptions(this.currentSearchTerm);
  }

  toggleDropdown() {
    this.isDropdownOpen = !this.isDropdownOpen;
    if (this.isDropdownOpen) {
      this.filterOptions(this.searchControl.value || '');
      this.searchTerms.next(this.searchControl.value || '');
    }
  }

  handleClickOutside() {
    this.isDropdownOpen = false;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
